/*
 ** Author: Nuno Alex
 ** Date: 09/2025
 ** Test: BBB-SEP02 | PDP | Upgrade to ICE Cool Bundle
 ** Variation: Dynamic Liquid Version
 */

(() => {
	const debugMode = 1,
		testId = "BBB-product",
		logInfo = testId + " -->",
		variation = 2;

	// Avoid code to run multiple times
	if (!document.documentElement.classList.contains(`${testId}_var_${variation}`)) {
		document.documentElement.classList.add(`${testId}_var_${variation}`);
		document.documentElement.classList.add(`${testId}_var`);

		// Tampermonkey has a specific window scope. This will make sure you access the correct website's "window" object.
		let _window = window;
		if (typeof unsafeWindow !== "undefined") _window = unsafeWindow;

		// Function to get the correct product URL prefix based on current page
		const getProductUrlPrefix = () => {
			try {
				const currentPath = _window.location.pathname;
				const pathParts = currentPath.split("/");

				if (pathParts.length >= 3 && pathParts[1] && pathParts[2] === "products") {
					return `/${pathParts[1]}/products/`;
				} else if (pathParts.length >= 2 && pathParts[1] === "products") {
					return "/products/";
				}

				return "/products/";
			} catch (err) {
				if (debugMode) console.error(logInfo, err.message);
			}
		};

		const productUrlPrefix = getProductUrlPrefix();

		// Dynamic product cache
		let productDataCache = {};

		// DYNAMIC VARIANT CONFIGURATION
		// This will be read from data attributes on the toggle element in Liquid
		// Expected format from Liquid:
		// data-variant-config='{
		//   "variant_mapping": {
		//     "variant_1": {
		//       "id": "123",
		//       "product_handle": "pillow",
		//       "redirect_url": "/products/pillow",  // optional, falls back to product_handle
		//       "to": "456",
		//       "to_product_handle": "icecool",
		//       "to_redirect_url": "/products/icecool"  // optional, falls back to to_product_handle
		//     },
		//     "variant_2": {
		//       "id": "789",
		//       "product_handle": "premium-bundle",
		//       "redirect_url": "/collections/bundles/products/premium",  // custom URL
		//       "to": "012",
		//       "to_product_handle": "ultimate-icecool",
		//       "to_redirect_url": "/collections/premium/products/ultimate-icecool"  // custom URL
		//     }
		//   }
		// }'
		let VARIANT_CONFIG = null;

		// Function to parse variant config from toggle element
		const parseVariantConfig = () => {
			try {
				const toggleElement = document.querySelector(`.${testId}_toggle-section`);
				if (!toggleElement) {
					if (debugMode) console.warn(logInfo, "Toggle element not found");
					return null;
				}

				const configData = toggleElement.getAttribute("data-variant-config");
				if (!configData) {
					if (debugMode) console.warn(logInfo, "Variant config not found on toggle element");
					return null;
				}

				const config = JSON.parse(configData);
				if (debugMode) console.log(logInfo, "Parsed variant config:", config);

				// Validate config structure
				if (!config.variant_mapping) {
					if (debugMode) console.error(logInfo, "Invalid config structure - variant_mapping required");
					return null;
				}

				return config;
			} catch (err) {
				if (debugMode) console.error(logInfo, "Error parsing variant config:", err);
				return null;
			}
		};

		const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

		// Cache for currency info
		let currencyInfo = null;

		// Flag to prevent redirects when we're programmatically selecting bundle options
		let isProgrammaticBundleSelection = false;

		// Detect current page type and get locale
		const getCurrentPageInfo = () => {
			const currentPath = _window.location.pathname;
			const currentUrl = _window.location.href;
			const urlParams = new URLSearchParams(_window.location.search);

			if (!VARIANT_CONFIG) {
				VARIANT_CONFIG = parseVariantConfig();
			}

			// Check if we're on an upgraded page by looking at URL param or checking if current variant is a "to" variant
			const isUpgradedPage = urlParams.has("upgrade_mode");

			// Extract locale from pathname
			const pathParts = currentPath.split("/").filter(Boolean);
			let locale = "";
			if (pathParts.length > 0) {
				const firstPart = pathParts[0].toLowerCase();
				if (firstPart.match(/^[a-z]{2}(-[a-z]{2})?$/)) {
					locale = `/${firstPart}`;
				}
			}

			// Get current variant from URL or form
			const variantFromUrl = urlParams.get("variant");
			const form = document.querySelector("form[id*='product-form']") || document.querySelector("form[action*='cart']");
			const variantFromForm = form?.querySelector('input[name="id"]')?.value;
			const currentVariantId = variantFromUrl || variantFromForm;

			return {
				isUpgradedPage,
				locale,
				currentVariantId,
				currentPath
			};
		};

		// Build redirect URL with proper localization
		// If customUrl is provided, use it; otherwise build from productHandle
		const buildRedirectUrl = (productHandle, variantId, addUpgradeParam = false, customUrl = null) => {
			const pageInfo = getCurrentPageInfo();
			const locale = pageInfo.locale || "";

			let baseUrl;
			if (customUrl) {
				// Use custom URL if provided (e.g., "/collections/premium/products/icecool")
				// Add locale if it doesn't already start with locale
				if (locale && !customUrl.startsWith(locale)) {
					baseUrl = `${locale}${customUrl}`;
				} else {
					baseUrl = customUrl;
				}
			} else {
				// Build standard product URL
				baseUrl = `${locale}/products/${productHandle}`;
			}

			const params = new URLSearchParams();
			params.set("variant", variantId);
			if (addUpgradeParam) {
				params.set("upgrade_mode", "1");
			}
			return `${baseUrl}?${params.toString()}`;
		};

		// Wait for an element(s) to be fully loaded in the DOM
		const waitForElement = (cssSelector, callback, multi = false, maxCalls = 300) => {
			try {
				let elementsCached;

				const interval = setInterval(() => {
					try {
						if (multi) {
							elementsCached = document.querySelectorAll(cssSelector);

							if (elementsCached.length) {
								clearInterval(interval);
								callback(elementsCached);
							}
						} else {
							elementsCached = document.querySelector(cssSelector);

							if (elementsCached) {
								clearInterval(interval);
								callback(elementsCached);
							}
						}

						if (typeof maxCalls === "number" && --maxCalls < 0) {
							clearInterval(interval);
						}
					} catch (err) {
						if (debugMode) console.error(logInfo, err.message);
					}
				}, 20);
			} catch (err) {
				if (debugMode) console.error(logInfo, err.message);
			}
		};

		// Currency and price formatting functions
		const getCurrencyInfo = async () => {
			if (currencyInfo) return currencyInfo;
			try {
				const root = window.Shopify?.routes?.root || "/";
				const response = await fetch(root + "cart.js");
				const cartData = await response.json();
				const currency = cartData.currency || "USD";
				const locale = document.documentElement.lang || navigator.language || "en";
				const parts = new Intl.NumberFormat(locale, {
					style: "currency",
					currency,
					currencyDisplay: "narrowSymbol"
				}).formatToParts(0);
				const symbol = parts.find((p) => p.type === "currency")?.value || "$";
				currencyInfo = { currency, symbol };
				if (debugMode) console.log(logInfo, "Currency info:", currencyInfo);
				return currencyInfo;
			} catch (err) {
				if (debugMode) console.warn(logInfo, "Error getting currency info:", err);
				currencyInfo = { currency: "USD", symbol: "$" };
				return currencyInfo;
			}
		};

		const formatPrice = async (price, currencyInfo = null) => {
			const info = currencyInfo;
			try {
				const locale = document.documentElement.lang || navigator.language || "en";
				return new Intl.NumberFormat(locale, {
					style: "currency",
					currency: info.currency,
					currencyDisplay: "narrowSymbol"
				}).format(price);
			} catch (err) {
				if (debugMode) console.warn(logInfo, "Error formatting price with Intl:", err);
				return `${info.symbol}${price.toFixed(2)}`;
			}
		};

		const setupIntersectionObserver = (target, threshold = 0.1) => {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							const waitForAnalytics = setInterval(() => {
								if (window.dataLayer && window.igEvents && window.clarity) {
									window.igEvents.push({ event: `test_${testId}_interaction` });
									window.dataLayer.push({ event: "ab_test_interaction", test_id: testId, test_variant: `variation${variation}` });
									window.clarity("set", "ab_test", `${testId}_variation${variation}`);
									clearInterval(waitForAnalytics);
								}
							}, 100);
							setTimeout(() => clearInterval(waitForAnalytics), 30000);
							observer.disconnect();
						}
					});
				},
				{ threshold }
			);
			observer.observe(target);
		};

		const getCurrentSelectedBundlePrice = () => {
			const priceElement = document.querySelector(".price__sale .price-item--sale") || document.querySelector(".price__regular .price-item--regular") || document.querySelector(".price-item--regular, .price-item--sale");

			if (priceElement) {
				const price = parseFloat(priceElement.textContent.replace(/[^0-9.]/g, ""));
				if (debugMode) console.log(logInfo, "Current selected bundle price from DOM:", price);
				return price;
			}
			return 0;
		};

		const updateToggleDescription = async (toProductPrice, fromProductPrice = null) => {
			// If fromProductPrice is provided, use it; otherwise get from DOM
			const currentSelectedPrice = fromProductPrice !== null ? fromProductPrice : getCurrentSelectedBundlePrice() * 100;
			const priceDifference = toProductPrice - currentSelectedPrice;

			const toggleDescription = document.querySelector(`.${testId}_toggle-description`);
			if (!toggleDescription) return;

			// Get base description from data attribute
			const baseDescription = toggleDescription.getAttribute("data-base-description") || "Upgrade to premium!";

			if (priceDifference > 1) {
				const formattedDifference = await formatPrice(priceDifference / 100, currencyInfo);
				toggleDescription.innerHTML = baseDescription.replace("{price}", `<strong>only +${formattedDifference}!</strong>`);
			} else {
				// Remove price placeholder if no price difference
				toggleDescription.innerHTML = baseDescription.replace(/\s*for\s*\{price\}/, "").replace("{price}", "");
			}

			if (debugMode) console.log(logInfo, "Updated toggle description:", toggleDescription.innerHTML);
		};

		const formatTitle = (title) => {
			if (!title || typeof title !== "string") return "";
			return title
				.split("|")[0]
				.trim()
				.split(" ")
				.map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ""))
				.join(" ");
		};

		// Generic function to fetch product data by handle
		const fetchProductByHandle = async (handle) => {
			try {
				// Check cache first
				if (productDataCache[handle]) {
					if (debugMode) console.log(logInfo, `Using cached product data for: ${handle}`);
					return productDataCache[handle];
				}

				if (debugMode) console.log(logInfo, `Fetching product: ${handle}`);

				const response = await fetch(`${productUrlPrefix}${handle}.js`);
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const productData = await response.json();

				// Process the data to match our expected structure
				const processedData = {
					id: productData.id,
					title: formatTitle(productData.title),
					handle: productData.handle,
					description: productData.description,
					images: productData.images || [],
					variants: productData.variants || [],
					// Store all variant prices for easy access
					variantPrices: {}
				};

				// Map variant IDs to prices
				productData.variants.forEach((variant) => {
					processedData.variantPrices[variant.id.toString()] = {
						price: variant.price,
						compareAtPrice: variant.compare_at_price
					};
				});

				// Cache the data
				productDataCache[handle] = processedData;

				if (debugMode) console.log(logInfo, `Product data fetched for ${handle}:`, processedData);

				return processedData;
			} catch (err) {
				if (debugMode) console.error(logInfo, `Error fetching product ${handle}:`, err);
				return null;
			}
		};

		// Get price for a specific variant from product data
		const getVariantPrice = (productData, variantId) => {
			if (!productData || !variantId) return 0;
			const variantPrice = productData.variantPrices[variantId.toString()];
			return variantPrice ? variantPrice.price : 0;
		};

		// Get currently selected variant ID by checking data-sku attributes
		const getCurrentlySelectedVariantId = (preferUrl = false) => {
			try {
				// If preferUrl is true or we're on an upgraded page, prioritize URL/form variant
				const pageInfo = getCurrentPageInfo();
				if ((preferUrl || pageInfo.isUpgradedPage) && pageInfo.currentVariantId) {
					if (debugMode) console.log(logInfo, "Selected variant from URL/form:", pageInfo.currentVariantId);
					return pageInfo.currentVariantId;
				}

				// Fallback: check for selected option with data-sku
				const selectedOption = document.querySelector(".BBB-UPSELL-option-with-colors.BBB-UPSELL-selected");
				if (selectedOption) {
					const sku = selectedOption.getAttribute("data-sku");
					if (sku && debugMode) console.log(logInfo, "Selected variant SKU from DOM:", sku);
					return sku;
				}

				// Last resort: try URL/form if we haven't already
				if (pageInfo.currentVariantId) {
					if (debugMode) console.log(logInfo, "Selected variant from URL/form (fallback):", pageInfo.currentVariantId);
					return pageInfo.currentVariantId;
				}

				return null;
			} catch (err) {
				if (debugMode) console.error(logInfo, "Error getting current variant ID:", err);
				return null;
			}
		};

		// Find the corresponding "to" variant and product handle based on current "from" variant ID
		const getMappedToVariant = (fromVariantId) => {
			if (!VARIANT_CONFIG || !VARIANT_CONFIG.variant_mapping) return null;

			// Search through variant mapping to find the from variant
			for (const [key, mapping] of Object.entries(VARIANT_CONFIG.variant_mapping)) {
				if (mapping.id === fromVariantId.toString()) {
					if (debugMode) console.log(logInfo, `Found mapping: ${fromVariantId} -> ${mapping.to} (${mapping.to_product_handle})`);
					return {
						variantId: mapping.to,
						productHandle: mapping.to_product_handle,
						redirectUrl: mapping.to_redirect_url || null
					};
				}
			}

			if (debugMode) console.warn(logInfo, `No mapping found for variant: ${fromVariantId}`);
			return null;
		};

		// Find the corresponding "from" variant and product handle based on current "to" variant ID
		const getMappedFromVariant = (toVariantId) => {
			if (!VARIANT_CONFIG || !VARIANT_CONFIG.variant_mapping) return null;

			// Search through variant mapping to find the to variant
			for (const [key, mapping] of Object.entries(VARIANT_CONFIG.variant_mapping)) {
				if (mapping.to === toVariantId.toString()) {
					if (debugMode) console.log(logInfo, `Found reverse mapping: ${toVariantId} -> ${mapping.id} (${mapping.product_handle})`);
					return {
						variantId: mapping.id,
						productHandle: mapping.product_handle,
						redirectUrl: mapping.redirect_url || null
					};
				}
			}

			if (debugMode) console.warn(logInfo, `No reverse mapping found for variant: ${toVariantId}`);
			return null;
		};

		// Get all unique product handles from variant mapping
		const getAllProductHandles = () => {
			if (!VARIANT_CONFIG || !VARIANT_CONFIG.variant_mapping) return [];

			const handles = new Set();
			for (const [key, mapping] of Object.entries(VARIANT_CONFIG.variant_mapping)) {
				if (mapping.product_handle) handles.add(mapping.product_handle);
				if (mapping.to_product_handle) handles.add(mapping.to_product_handle);
			}

			return Array.from(handles);
		};

		// Fetch all products based on config variant mappings
		const fetchConfiguredProducts = async () => {
			if (!VARIANT_CONFIG) {
				if (debugMode) console.error(logInfo, "No variant config available");
				return null;
			}

			try {
				if (debugMode) console.log(logInfo, "Fetching configured products...");

				// Get all unique product handles from the mapping
				const handles = getAllProductHandles();

				// Fetch all products in parallel
				const productPromises = handles.map(handle => fetchProductByHandle(handle));
				const products = await Promise.all(productPromises);

				// Create a map of handle -> product data
				const productMap = {};
				products.forEach((product, index) => {
					if (product) {
						productMap[handles[index]] = product;
					}
				});

				if (debugMode) console.log(logInfo, "Configured products fetched:", Object.keys(productMap));

				return productMap;
			} catch (err) {
				if (debugMode) console.error(logInfo, "Error fetching configured products:", err);
				return null;
			}
		};

		// Function to handle variant option selection changes
		const handleVariantOptionChange = async (event) => {
			try {
				// Check if this is a variant option click
				const variantOption = event.target.closest(".BBB-UPSELL-option-with-colors");
				if (!variantOption) return;

				// Small delay to let the variant selection update the DOM
				await new Promise((resolve) => setTimeout(resolve, 100));

				const pageInfo = getCurrentPageInfo();

				// If on upgraded page and variant changed, update pricing AND URL
				if (pageInfo.isUpgradedPage) {
					// Skip if this is a programmatic selection
					if (isProgrammaticBundleSelection) {
						if (debugMode) console.log(logInfo, "Skipping - programmatic variant selection");
						return;
					}

					// Wait for DOM to update
					await new Promise((resolve) => setTimeout(resolve, 200));

					// Get currently selected to_variant from DOM (user just clicked it)
					const selectedOption = document.querySelector(".BBB-UPSELL-option-with-colors.BBB-UPSELL-selected");
					if (!selectedOption) return;

					const currentToVariantId = selectedOption.getAttribute("data-sku");
					if (!currentToVariantId) return;

					if (debugMode) console.log(logInfo, "User selected variant on upgraded page:", currentToVariantId);

					// Update URL with new variant (without reload)
					const currentUrl = new URL(_window.location.href);
					currentUrl.searchParams.set("variant", currentToVariantId);
					_window.history.replaceState({}, "", currentUrl.toString());
					if (debugMode) console.log(logInfo, "Updated URL to reflect new variant");

					// Find corresponding from_variant
					const fromVariant = getMappedFromVariant(currentToVariantId);
					if (!fromVariant) return;

					// Fetch products and update pricing
					const productMap = await fetchConfiguredProducts();
					if (productMap) {
						const fromProduct = productMap[fromVariant.productHandle];

						// Find the mapping for the current to variant to get its product handle
						let toProductHandle = null;
						for (const mapping of Object.values(VARIANT_CONFIG.variant_mapping)) {
							if (mapping.to === currentToVariantId.toString()) {
								toProductHandle = mapping.to_product_handle;
								break;
							}
						}

						const toProduct = toProductHandle ? productMap[toProductHandle] : null;

						if (fromProduct && toProduct) {
							const fromPrice = getVariantPrice(fromProduct, fromVariant.variantId);
							const toPrice = getVariantPrice(toProduct, currentToVariantId);

							await updateToggleDescription(toPrice, fromPrice);
							if (debugMode) console.log(logInfo, "Updated pricing on upgraded page after variant change");
						}
					}
					return;
				}

				// On original page - update pricing
				if (debugMode) console.log(logInfo, "Variant option changed on original page");

				// Wait for form to update
				let attempts = 0;
				const maxAttempts = 10;

				while (attempts < maxAttempts) {
					await new Promise((resolve) => setTimeout(resolve, 50));

					const currentFromVariantId = getCurrentlySelectedVariantId();
					if (!currentFromVariantId) {
						attempts++;
						continue;
					}

					// Find corresponding to_variant
					const toVariant = getMappedToVariant(currentFromVariantId);
					if (!toVariant) break;

					const productMap = await fetchConfiguredProducts();
					if (productMap) {
						const toProduct = productMap[toVariant.productHandle];
						if (toProduct) {
							const toPrice = getVariantPrice(toProduct, toVariant.variantId);
							await updateToggleDescription(toPrice);
							break;
						}
					}

					attempts++;
				}

				// If toggle is checked and on original page, redirect
				const toggleInput = document.querySelector(`.${testId}_toggle-input`);
				if (toggleInput && toggleInput.checked) {
					setTimeout(() => {
						const currentFromVariantId = getCurrentlySelectedVariantId();
						if (!currentFromVariantId) return;

						const toVariant = getMappedToVariant(currentFromVariantId);
						if (!toVariant) return;

						const redirectUrl = buildRedirectUrl(toVariant.productHandle, toVariant.variantId, true, toVariant.redirectUrl);
						if (debugMode) console.log(logInfo, "Redirecting after variant change:", redirectUrl);
						_window.location.href = redirectUrl;
					}, 200);
				}
			} catch (err) {
				if (debugMode) console.error(logInfo, err.message);
			}
		};

		let variantChangeListenerSetup = false;
		const setupVariantChangeListeners = () => {
			if (variantChangeListenerSetup) return;
			document.addEventListener("click", handleVariantOptionChange);
			variantChangeListenerSetup = true;
			if (debugMode) console.log(logInfo, "Variant option change listener setup");
		};

		const handleToggleChange = async (event) => {
			const isChecked = event.target.checked;
			const pageInfo = getCurrentPageInfo();

			if (debugMode) console.log(logInfo, "Toggle changed:", isChecked, "Page info:", pageInfo);

			try {
				if (pageInfo.isUpgradedPage) {
					// On upgraded page - toggle OFF means downgrade
					if (!isChecked) {
						// Track downgrade event
						if (window.igEvents) {
							window.igEvents.push({ event: `${testId}_toggle_downgrade` });
							if (debugMode) console.log(logInfo, "Tracked toggle downgrade event");
						}

						// Get current to_variant and find corresponding from_variant
						const currentToVariantId = getCurrentlySelectedVariantId();
						const fromVariant = getMappedFromVariant(currentToVariantId);

						if (fromVariant) {
							const redirectUrl = buildRedirectUrl(fromVariant.productHandle, fromVariant.variantId, false, fromVariant.redirectUrl);
							if (debugMode) console.log(logInfo, "Redirecting to original:", redirectUrl);
							_window.location.href = redirectUrl;
						}
					}
				} else {
					// On original page - toggle ON means upgrade
					if (isChecked) {
						// Track upgrade event
						if (window.igEvents) {
							window.igEvents.push({ event: `${testId}_toggle_upgrade` });
							if (debugMode) console.log(logInfo, "Tracked toggle upgrade event");
						}

						// Get current from_variant and find corresponding to_variant
						const currentFromVariantId = getCurrentlySelectedVariantId();
						const toVariant = getMappedToVariant(currentFromVariantId);

						if (toVariant) {
							const redirectUrl = buildRedirectUrl(toVariant.productHandle, toVariant.variantId, true, toVariant.redirectUrl);
							if (debugMode) console.log(logInfo, "Redirecting to upgraded:", redirectUrl);
							_window.location.href = redirectUrl;
						}
					}
				}
			} catch (err) {
				if (debugMode) console.error(logInfo, "Error handling toggle:", err);
			}
		};

		// Function to ensure correct variant option is selected based on URL variant
		const ensureCorrectVariantSelection = async () => {
			const pageInfo = getCurrentPageInfo();
			if (!pageInfo.currentVariantId || !VARIANT_CONFIG) {
				if (debugMode) console.log(logInfo, "No variant ID in URL or no config");
				return;
			}

			if (debugMode) console.log(logInfo, "Ensuring variant selection for:", pageInfo.currentVariantId);

			// Check if the correct variant is already selected by checking data-sku
			const selectedOption = document.querySelector(".BBB-UPSELL-option-with-colors.BBB-UPSELL-selected");
			if (selectedOption) {
				const selectedSku = selectedOption.getAttribute("data-sku");
				if (selectedSku === pageInfo.currentVariantId) {
					if (debugMode) console.log(logInfo, "Correct variant already selected:", selectedSku);
					return;
				}
			}

			// Wait a bit for the variant options to be fully rendered
			await new Promise((resolve) => setTimeout(resolve, 200));

			// Find the option with matching data-sku
			const targetOption = document.querySelector(`.BBB-UPSELL-option-with-colors[data-sku="${pageInfo.currentVariantId}"]`);
			if (!targetOption) {
				if (debugMode) console.warn(logInfo, "No option found with data-sku:", pageInfo.currentVariantId);
				return;
			}

			// Click the option to select it
			let attempts = 0;
			const maxAttempts = 15;

			const trySelectVariant = () => {
				if (targetOption.classList.contains("BBB-UPSELL-selected")) {
					if (debugMode) console.log(logInfo, "Variant option successfully selected");
					return;
				}

				isProgrammaticBundleSelection = true;

				if (debugMode) console.log(logInfo, `Selecting variant ${pageInfo.currentVariantId} (attempt ${attempts + 1}/${maxAttempts})`);
				targetOption.click();

				setTimeout(() => {
					isProgrammaticBundleSelection = false;
				}, 1000);

				attempts++;

				if (attempts < maxAttempts) {
					setTimeout(() => {
						if (!targetOption.classList.contains("BBB-UPSELL-selected")) {
							trySelectVariant();
						} else {
							isProgrammaticBundleSelection = false;
						}
					}, 200);
				} else {
					isProgrammaticBundleSelection = false;
					if (debugMode) console.warn(logInfo, "Failed to select variant after max attempts");
				}
			};

			setTimeout(() => {
				trySelectVariant();
			}, 100);
		};

		// Function to check if current page is a "to" product (upgraded product)
		const isToProduct = () => {
			if (!VARIANT_CONFIG || !VARIANT_CONFIG.variant_mapping) return false;

			const pageInfo = getCurrentPageInfo();
			if (!pageInfo.currentVariantId) return false;

			// Check if current variant is a "to" variant in any mapping
			for (const mapping of Object.values(VARIANT_CONFIG.variant_mapping)) {
				if (mapping.to === pageInfo.currentVariantId.toString()) {
					return true;
				}
			}
			return false;
		};

		// Function to show/hide toggle based on page context
		const handleToggleVisibility = () => {
			const toggleSection = document.querySelector(`.${testId}_toggle-section`);
			if (!toggleSection) return;

			const pageInfo = getCurrentPageInfo();

			// Check if we're on a "to" product page (icecool) without upgrade_mode
			if (isToProduct() && !pageInfo.isUpgradedPage) {
				// Hide toggle on "to" product pages when not in upgrade mode
				toggleSection.style.display = 'none';
				if (debugMode) console.log(logInfo, "Hiding toggle - on 'to' product without upgrade_mode");
			} else {
				// Show toggle on all other pages
				toggleSection.style.display = '';
				if (debugMode) console.log(logInfo, "Showing toggle");
			}
		};

		// Function to show toggle after initialization
		const showToggleAfterInit = () => {
			const toggleSection = document.querySelector(`.${testId}_toggle-section`);
			if (toggleSection) {
				toggleSection.classList.add('BBB-product_toggle-ready');
				if (debugMode) console.log(logInfo, "Toggle visible after initialization");
			}
		};

		// Main initialization function
		const initializeToggle = async () => {
			try {
				// Parse config from toggle element
				VARIANT_CONFIG = parseVariantConfig();
				if (!VARIANT_CONFIG) {
					if (debugMode) console.error(logInfo, "Cannot initialize - no variant config");
					return;
				}

				const pageInfo = getCurrentPageInfo();
				const isOnUpgradedPage = pageInfo.isUpgradedPage;

				if (debugMode) console.log(logInfo, "Initializing toggle", isOnUpgradedPage ? "(on upgraded page)" : "(on original page)");

				// Handle toggle visibility based on page context (but don't show yet)
				handleToggleVisibility();

				// Get currency info
				await getCurrencyInfo();

				// Set up variant change listeners
				setupVariantChangeListeners();

				if (!isOnUpgradedPage) {
					// On original page (e.g., pillow page)
					// Check if there's a variant in the URL (e.g., coming back from icecool)
					if (pageInfo.currentVariantId) {
						// Check if this variant ID exists in our mapping
						const isVariantInMapping = Object.values(VARIANT_CONFIG.variant_mapping).some(
							mapping => mapping.id === pageInfo.currentVariantId
						);

						if (isVariantInMapping) {
							if (debugMode) console.log(logInfo, "Variant in URL found in mapping, ensuring selection");
							// Ensure the correct variant option is selected
							await ensureCorrectVariantSelection();
							// Wait for selection to complete
							await new Promise((resolve) => setTimeout(resolve, 300));
						}
					}

					// Fetch products and calculate price difference
					const currentFromVariantId = getCurrentlySelectedVariantId();
					if (currentFromVariantId) {
						const toVariant = getMappedToVariant(currentFromVariantId);
						if (toVariant) {
							const productMap = await fetchConfiguredProducts();
							if (productMap) {
								const toProduct = productMap[toVariant.productHandle];
								if (toProduct) {
									const toPrice = getVariantPrice(toProduct, toVariant.variantId);
									await updateToggleDescription(toPrice);
								}
							}
						}
					}
				} else {
					// On upgraded page - ensure correct variant selection
					await ensureCorrectVariantSelection();

					// Wait for variant selection to settle
					await new Promise((resolve) => setTimeout(resolve, 500));

					// Get current to_variant and find corresponding from_variant
					const currentToVariantId = getCurrentlySelectedVariantId();
					if (currentToVariantId) {
						const fromVariant = getMappedFromVariant(currentToVariantId);
						if (fromVariant) {
							// Find the to_product_handle for this variant
							let toProductHandle = null;
							for (const mapping of Object.values(VARIANT_CONFIG.variant_mapping)) {
								if (mapping.to === currentToVariantId.toString()) {
									toProductHandle = mapping.to_product_handle;
									break;
								}
							}

							// Fetch products and calculate price difference
							const productMap = await fetchConfiguredProducts();
							if (productMap && toProductHandle) {
								const fromProduct = productMap[fromVariant.productHandle];
								const toProduct = productMap[toProductHandle];

								if (fromProduct && toProduct) {
									const fromPrice = getVariantPrice(fromProduct, fromVariant.variantId);
									const toPrice = getVariantPrice(toProduct, currentToVariantId);

									await updateToggleDescription(toPrice, fromPrice);
								}
							}
						}
					}
				}

				// Add event listener for toggle
				const toggleInput = document.querySelector(`.${testId}_toggle-input`);
				if (toggleInput) {
					toggleInput.addEventListener("change", handleToggleChange);
					if (debugMode) console.log(logInfo, "Toggle event listener attached");
				}

				// Setup analytics tracking
				const toggleSection = document.querySelector(`.${testId}_toggle-section`);
				if (toggleSection) {
					if (isMobile) {
						_window.addEventListener("scroll", function onScroll() {
							_window.removeEventListener("scroll", onScroll);
							setupIntersectionObserver(toggleSection);
						});
					} else {
						setupIntersectionObserver(toggleSection);
					}
				}

				// Show toggle after all initialization is complete
				showToggleAfterInit();

				if (debugMode) console.log(logInfo, "Toggle initialized successfully");
			} catch (err) {
				if (debugMode) console.error(logInfo, "Error initializing toggle:", err);
			}
		};

		// Wait for toggle element to be present in DOM (added by Liquid)
		waitForElement(`.${testId}_toggle-section`, async (toggleElement) => {
			await initializeToggle();
		});
	}
})();
